import type { ChangeEvent, ReactNode } from 'react';

/**
 * Campos de formulário.
 *
 * Todos compartilham a mesma moldura para que um formulário misto não pareça
 * remendado. O erro fica ABAIXO do campo e em vermelho de estado — nunca só a borda
 * vermelha, que não diz o que está errado.
 */

const MOLDURA =
  'w-full rounded-md border bg-[color:var(--superficie-elevada)] px-3 py-2 text-sm ' +
  'text-[color:var(--texto)] placeholder:text-[color:var(--texto-tenue)] ' +
  'focus:border-[color:var(--acento)] focus:outline-none disabled:opacity-50';

function Envolucro({
  rotulo,
  erro,
  auxilio,
  children,
}: {
  rotulo?: string;
  erro?: string;
  auxilio?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      {rotulo && (
        <span className="mb-1 block text-xs font-medium text-[color:var(--texto-suave)]">
          {rotulo}
        </span>
      )}
      {children}
      {erro && (
        <span
          className="mt-1 block text-xs"
          style={{ color: 'var(--color-vencido)' }}
          role="alert"
        >
          {erro}
        </span>
      )}
      {!erro && auxilio && (
        <span className="mt-1 block text-xs text-[color:var(--texto-tenue)]">{auxilio}</span>
      )}
    </label>
  );
}

export function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = 'text',
  placeholder,
  erro,
  auxilio,
  desabilitado,
}: {
  rotulo?: string;
  valor: string;
  aoMudar: (v: string) => void;
  tipo?: string;
  placeholder?: string;
  erro?: string;
  auxilio?: string;
  desabilitado?: boolean;
}) {
  return (
    <Envolucro {...(rotulo ? { rotulo } : {})} {...(erro ? { erro } : {})} {...(auxilio ? { auxilio } : {})}>
      <input
        type={tipo}
        value={valor}
        onChange={(e: ChangeEvent<HTMLInputElement>) => aoMudar(e.target.value)}
        placeholder={placeholder}
        disabled={desabilitado}
        className={MOLDURA}
        style={erro ? { borderColor: 'var(--color-vencido)' } : undefined}
      />
    </Envolucro>
  );
}

export interface Opcao {
  readonly valor: string;
  readonly rotulo: string;
}

export function Seletor({
  rotulo,
  valor,
  aoMudar,
  opcoes,
  vazio,
  erro,
}: {
  rotulo?: string;
  valor: string;
  aoMudar: (v: string) => void;
  opcoes: readonly Opcao[];
  /** Rótulo da opção vazia. Omitido, o campo passa a ser obrigatório na prática. */
  vazio?: string;
  erro?: string;
}) {
  return (
    <Envolucro {...(rotulo ? { rotulo } : {})} {...(erro ? { erro } : {})}>
      <select
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        className={MOLDURA}
        style={erro ? { borderColor: 'var(--color-vencido)' } : undefined}
      >
        {vazio !== undefined && <option value="">{vazio}</option>}
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
    </Envolucro>
  );
}

export function AreaDeTexto({
  rotulo,
  valor,
  aoMudar,
  linhas = 4,
  placeholder,
  auxilio,
  fonteMono = false,
}: {
  rotulo?: string;
  valor: string;
  aoMudar: (v: string) => void;
  linhas?: number;
  placeholder?: string;
  auxilio?: string;
  fonteMono?: boolean;
}) {
  return (
    <Envolucro {...(rotulo ? { rotulo } : {})} {...(auxilio ? { auxilio } : {})}>
      <textarea
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        rows={linhas}
        placeholder={placeholder}
        className={`${MOLDURA} resize-y leading-relaxed ${fonteMono ? 'font-mono text-xs' : ''}`}
      />
    </Envolucro>
  );
}

export function Caixa({
  rotulo,
  marcado,
  aoMudar,
  desabilitado,
}: {
  rotulo: ReactNode;
  marcado: boolean;
  aoMudar: (v: boolean) => void;
  desabilitado?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
      <input
        type="checkbox"
        checked={marcado}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.checked)}
        className="size-4 shrink-0 rounded accent-[color:var(--acento)] disabled:opacity-40"
      />
      <span className="text-[color:var(--texto)]">{rotulo}</span>
    </label>
  );
}
